import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router";

import { router } from "../app/services/router";
import { tracking } from "../app/services/tracking";
import {
  AccountSignUpForm,
  SignUpState,
  SignUpStateType,
} from "../features/account/AccountSignUpForm";
import { Dialog } from "../features/shared/Dialog";
import { ReCaptcha } from "../features/shared/ReCaptcha";

export const SignUpPage = () => {
  const to = router.getTo("authenticationSignUp");

  const location = useLocation();
  const navigate = useNavigate();

  return (
    <Dialog
      open={to === location.pathname}
      onOpenChange={(open) => {
        if (!open) {
          navigate(router.getTo("home"));
        }
      }}
    >
      <SignUpPageContent trigger={document.querySelector(`[href*="${to}"]`)} />
    </Dialog>
  );
};

type SignUpPageContentProps = { trigger: HTMLElement | null };

const SignUpPageContent = (props: SignUpPageContentProps) => {
  const { trigger } = props;

  const [searchParams] = useSearchParams();

  const [state, setState] = React.useState({} as SignUpState);

  const { t } = useTranslation();
  const { meta, title } = t("pages.SignUpPage") as unknown as {
    meta: { title: string };
    title: string;
  };

  React.useEffect(() => {
    const dataLayer = {
      [SignUpStateType.CONFIRM_ACCOUNT]: {
        nom_page: "confirmation_compte",
        virtualpagetitle: "Homepage déconnectée - Confirmation compte",
      },
      [SignUpStateType.CONFIRM_EMAIL]: {
        nom_page: "confirmation_mail",
        virtualpagetitle: "Homepage déconnectée - Confirmation mail",
      },
      [SignUpStateType.CONFIRM_SIGN_UP]: {
        nom_page: "confirmation_envoi_mail",
        virtualpagetitle: "Homepage déconnectée - Confirmation envoi mail",
      },
      [SignUpStateType.GET_ACCOUNT_WITH_CHARACTER_CODE]: {
        nom_page: "saisie_reference_activation",
        virtualpagetitle: "Homepage déconnectée - Saisie reference activation",
      },
      [SignUpStateType.GET_ACCOUNT_WITH_EMAIL]: {
        nom_page: "saisie_donnees_personnelles",
        virtualpagetitle: "Homepage déconnectée - Saisie données personnelles",
      },
      [SignUpStateType.RESULT]: {
        nom_page: "fin",
        virtualpagetitle: "Homepage déconnectée - Fin",
      },
    }[state.step];
    tracking.trackPage({ ...dataLayer, categorie_page: "homepage" });
  }, [state.step]);

  return (
    <Dialog.Content
      meta={meta}
      size={state.step === SignUpStateType.CONFIRM_ACCOUNT ? "large" : "medium"}
      title={title}
      variant="account"
      onCloseAutoFocus={() => trigger?.focus()}
      onOpenAutoFocus={() =>
        setState(
          searchParams.get("activationCode") && searchParams.get("username")
            ? {
                step: SignUpStateType.CONFIRM_SIGN_UP,
                session: {
                  code: String(searchParams.get("activationCode")),
                  username: String(searchParams.get("username")),
                },
              }
            : { step: SignUpStateType.GET_ACCOUNT_WITH_EMAIL },
        )
      }
    >
      <AccountSignUpForm state={state} onChange={setState} />
      <ReCaptcha />
    </Dialog.Content>
  );
};
